import { lazy } from "react";
import MathWorkSheet from "../Components/Courses/Maths";
import Sections from "../Components/Courses/Sections/Index";
import NewExerciseTracker from "../Components/NewTracker/NewExerciseTracker";
import ExerciseTrackerCatalog from "../Components/Tracker/ExerciseTrackerCatalog";
import AppPathCreationContainer from "../Components/HomePage/LearningPath/AppPathCreationContainer";
import AppEditDemoContainer from "../Components/GuestUserDashboard/EditClassFlow/AppEditDemoContainer";
import PaidStudentHome from "../Components/PaidStudentHome/PaidStudentHome";

export const MentorDashboard = lazy(
  () => import("../Components/Mentor/MentorDashboard"),
);
export const DetailedInsight = lazy(
  () => import("../Components/Evaluation/Insights/DetailedInsights"),
);
export const EvaluationResults = lazy(
  () => import("../Components/Evaluation/Insights/Scores/ScorePage"),
);
export const LanguageLearning = lazy(
  () => import("../Components/LanguageLearning/LanguageLearningPage"),
);
export const Workbench = lazy(
  () => import("../Components/EditorWithOutput/EditorWithOutput"),
);
export const Home = lazy(() => import("../Components/Home/HomeNew"));
export const TopicLevels = lazy(
  () => import("../Components/HomePage/userLearningGoals/index"),
);
export const Collection = lazy(
  () => import("../Components/Courses/Collection"),
);
export const FilesRouter = lazy(
  () => import("../Components/Files/FilesRouter"),
);
export const LearningContent = lazy(
  () => import("../Components/LearningContent/LearningContent"),
);
export const NewProjectPage = lazy(
  () => import("../Components/NewProjectPage"),
);
export const Courses = lazy(() => import("../Components/Courses"));
export const Exercises = lazy(() => import("../Components/Courses/exercises"));
export const CompletedHomework = lazy(
  () => import("../Components/Courses/AssignedHomework/CompletedHomeworkPage"),
);
export const ExerciseWorkbench = lazy(
  () => import("../Components/Courses/workbench"),
);
export const Whiteboard = lazy(() => import("../Components/Whiteboard"));
export const Evaluation = lazy(() => import("../Components/Evaluation"));
export const QuestionBank = lazy(() => import("../Components/QuestionBank"));
export const ViewInsights = lazy(
  () =>
    import("../Components/HomePage/HomePaidUser/Components/ViewInsights/Index"),
);
export const SPDashboard = lazy(
  () => import("../Components/SPDashboard/SPDashboard"),
);

export const GuestUserDashboard = lazy(
  () => import("../Components/GuestUserDashboard/index"),
);

export const LandingPage = lazy(
  () => import("../Components/GuestUserDashboard/LandingPage/index"),
);
// ---------------------------------------------------------------------------
// Protected route definitions
// Returns the route config array for the authenticated area of the app.
// @param {string} role - The current user's role (e.g. "student", "mentor", "guest", "admin")
// @param {boolean} isPaidStudent - paid students get PaidStudentHome, which picks
//   between the new homepage and the old one
// ---------------------------------------------------------------------------
export function getAppRoutes(role, isPaidStudent) {
  const homeComponent =
    (role === "student" && !isPaidStudent) || role === "guest"
      ? GuestUserDashboard
      : role === "student" && !!isPaidStudent
        ? PaidStudentHome
        : role === "mentor"
          ? MentorDashboard
          : Home;

  return [
    { path: "/home", component: homeComponent, exact: true, allow: true },
    // Keep: StudentPortal's SandboxRenderer hard-codes /sp-dashboard as the route
    // that renders its own /dashboard. Removing it breaks the portal's landing page.
    { path: "/sp-dashboard", component: SPDashboard, exact: true, allow: true },
    { path: "/new", component: NewProjectPage, exact: true, allow: true },
    { path: "/workbench", component: Workbench, exact: true, allow: true },
    {
      path: "/workbench/personalCollection/:fileId",
      component: Workbench,
      exact: true,
      allow: true,
    },
    {
      path: "/workbench/classNote/:fileId",
      component: Workbench,
      exact: true,
      allow: true,
    },
    {
      path: "/workbench/assignment/:fileId",
      component: Workbench,
      exact: true,
      allow: true,
    },
    {
      path: "/workbench/exercise/:fileId",
      component: Workbench,
      exact: true,
      allow: true,
    },
    { path: "/files", component: FilesRouter, exact: false, allow: true },
    {
      path: "/learningContent",
      component: LearningContent,
      exact: true,
      allow: true,
    },

    // Exercises
    { path: "/exercises", component: Courses, exact: true, allow: true },
    {
      path: "/exercises/assignedToMe",
      component: Courses,
      exact: true,
      allow: true,
    },
    {
      path: "/exercises/assignedToMe/completed-ai-homework",
      component: CompletedHomework,
      exact: true,
      allow: role === "student",
    },
    {
      path: "/exercises/:catalogId",
      component: Exercises,
      exact: true,
      allow: true,
    },
    {
      path: "/exercises/assignedToMe/:catalogId",
      component: Exercises,
      exact: true,
      allow: true,
    },
    {
      path: "/exercises/goals/:topicId",
      component: Exercises,
      exact: true,
      allow: true,
    },
    {
      path: "/exercises/maths/:catalogId/:exerciseId",
      component: MathWorkSheet,
      exact: true,
      allow: true,
    },
    {
      path: "/exercises/sections/:catalogId/:exerciseId",
      component: Sections,
      exact: true,
      allow: true,
    },
    {
      path: "/exercises/collection/:collectionId",
      component: Collection,
      exact: true,
      allow: true,
    },
    {
      path: "/exercises/collection/assignedToMe/:collectionId",
      component: Collection,
      exact: true,
      allow: true,
    },
    {
      path: "/exercises/:catalogId/:exerciseId",
      component: ExerciseWorkbench,
      exact: true,
      allow: true,
    },

    // Tutorials — same components as exercises, different path prefix
    { path: "/tutorials", component: Courses, exact: true, allow: true },
    {
      path: "/tutorials/assignedToMe",
      component: Courses,
      exact: true,
      allow: true,
    },
    {
      path: "/tutorials/:catalogId",
      component: Exercises,
      exact: true,
      allow: true,
    },
    {
      path: "/tutorials/assignedToMe/:catalogId",
      component: Exercises,
      exact: true,
      allow: true,
    },
    {
      path: "/tutorials/goals/:topicId",
      component: Exercises,
      exact: true,
      allow: true,
    },
    {
      path: "/tutorials/maths/:catalogId/:exerciseId",
      component: MathWorkSheet,
      exact: true,
      allow: true,
    },
    {
      path: "/tutorials/sections/:catalogId/:exerciseId",
      component: Sections,
      exact: true,
      allow: true,
    },
    {
      path: "/tutorials/collection/:collectionId",
      component: Collection,
      exact: true,
      allow: true,
    },
    {
      path: "/tutorials/collection/assignedToMe/:collectionId",
      component: Collection,
      exact: true,
      allow: true,
    },
    {
      path: "/tutorials/:catalogId/:exerciseId",
      component: ExerciseWorkbench,
      exact: true,
      allow: true,
    },

    // Whiteboard
    {
      path: "/whiteboard/:type/:fileId",
      component: Whiteboard,
      exact: true,
      allow: true,
    },
    { path: "/whiteboard", component: Whiteboard, exact: true, allow: true },

    // Role-restricted routes
    {
      path: "/question-bank",
      component: QuestionBank,
      exact: true,
      allow: role === "admin",
    },
    {
      path: "/tracker",
      component: NewExerciseTracker,
      exact: true,
      allow: role === "mentor",
    },
    {
      path: "/tracker/:id",
      component: ExerciseTrackerCatalog,
      exact: true,
      allow: role === "mentor",
    },
    {
      path: "/new-learning-path",
      component: AppPathCreationContainer,
      exact: true,
      allow: role === "guest",
    },
    {
      path: "/edit-demo",
      component: AppEditDemoContainer,
      exact: true,
      allow: role === "guest",
    },

    // Learning goals
    {
      path: "/goal/levels/:subject",
      component: TopicLevels,
      exact: true,
      allow: true,
    },
    {
      path: "/goal/levels/:subject/:grade",
      component: TopicLevels,
      exact: true,
      allow: true,
    },

    // Evaluation
    {
      path: "/evaluation/scores/:assessmentId",
      component: EvaluationResults,
      exact: true,
      allow: true,
    },
    {
      path: "/evaluation/insights/:assessmentId",
      component: DetailedInsight,
      exact: true,
      allow: true,
    },
    { path: "/evaluation", component: Evaluation, exact: false, allow: true },

    // Insights & learning
    {
      path: "/view-insights",
      component: ViewInsights,
      exact: true,
      allow: true,
    },
    {
      path: "/language-learning",
      component: LanguageLearning,
      exact: true,
      allow: true,
    },
  ];
}
